import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp, SectionLabel } from "./Motion";
import { useContent, resolveImg } from "../context/ContentContext";

const isVideo = (item) => {
  if (item.kind === "video") return true;
  const u = (item.url || "").toLowerCase();
  return u.endsWith(".mp4") || u.endsWith(".webm") || u.endsWith(".mov");
};

const Gallery = () => {
  const { content } = useContent();
  const items = (content.gallery || []).slice(0, 10);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const count = items.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % Math.max(count, 1)), [count]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + count) % Math.max(count, 1)), [count]);

  useEffect(() => {
    if (!count || paused) return undefined;
    const current = items[idx];
    if (isVideo(current)) return undefined; // let video advance on ended
    const t = setTimeout(next, 5000);
    return () => clearTimeout(t);
  }, [idx, count, paused, items, next]);

  if (!count) return null;

  return (
    <section
      id="gallery"
      className="relative py-28 md:py-40 bg-white"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <FadeUp>
          <SectionLabel>Moments</SectionLabel>
        </FadeUp>
        <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <FadeUp delay={0.05} className="md:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-[-0.02em] text-black">
              A decade <br />
              <span className="font-semibold">in pictures.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="md:col-span-4 text-zinc-600 md:text-right">
            A small archive of the people, the milestones, and the moments behind
            BELIEVE TEAM.
          </FadeUp>
        </div>

        <FadeUp delay={0.1}>
          <div
            className="relative mt-14 md:mt-20 rounded-[2rem] md:rounded-[2.5rem] overflow-hidden bg-zinc-100 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="gallery-stage"
          >
            <div className="relative aspect-[16/10] md:aspect-[21/9]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.99 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                >
                  {isVideo(items[idx]) ? (
                    <video
                      src={resolveImg(items[idx].url)}
                      autoPlay
                      muted
                      playsInline
                      onEnded={next}
                      className="w-full h-full object-cover"
                      data-testid={`gallery-video-${idx}`}
                    />
                  ) : (
                    <img
                      src={resolveImg(items[idx].url)}
                      alt={items[idx].caption || `Moment ${idx + 1}`}
                      className="w-full h-full object-cover"
                      data-testid={`gallery-image-${idx}`}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                </motion.div>
              </AnimatePresence>

              {/* Caption */}
              {items[idx].caption && (
                <div className="absolute bottom-6 md:bottom-10 left-6 md:left-12 right-24 md:right-32 text-white">
                  <div className="text-[10px] md:text-xs font-medium uppercase tracking-[0.28em] opacity-80">
                    {String(idx + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
                  </div>
                  <div className="font-display text-xl md:text-3xl mt-2 max-w-2xl">
                    {items[idx].caption}
                  </div>
                </div>
              )}

              {/* Arrows */}
              {count > 1 && (
                <>
                  <button
                    onClick={prev}
                    aria-label="Previous"
                    className="absolute top-1/2 -translate-y-1/2 left-3 md:left-5 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/85 backdrop-blur text-black flex items-center justify-center hover:scale-105 transition-transform"
                    data-testid="gallery-prev"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M9 1L3 7L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  <button
                    onClick={next}
                    aria-label="Next"
                    className="absolute top-1/2 -translate-y-1/2 right-3 md:right-5 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/85 backdrop-blur text-black flex items-center justify-center hover:scale-105 transition-transform"
                    data-testid="gallery-next"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M5 1L11 7L5 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Progress bar */}
            {count > 1 && !isVideo(items[idx]) && !paused && (
              <motion.div
                key={`progress-${idx}`}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 5, ease: "linear" }}
                className="absolute bottom-0 left-0 h-[2px] bg-white/80"
              />
            )}
          </div>
        </FadeUp>

        {/* Thumbnails */}
        {count > 1 && (
          <div className="mt-6 md:mt-8 flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="gallery-thumbs">
            {items.map((it, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`relative flex-none w-24 md:w-32 aspect-[16/10] rounded-xl overflow-hidden transition-all duration-300 ${
                  i === idx ? "ring-2 ring-black opacity-100" : "opacity-60 hover:opacity-100"
                }`}
                aria-label={`Show item ${i + 1}`}
                data-testid={`gallery-thumb-${i}`}
              >
                {isVideo(it) ? (
                  <video src={resolveImg(it.url)} muted className="w-full h-full object-cover" />
                ) : (
                  <img src={resolveImg(it.url)} alt="" className="w-full h-full object-cover" />
                )}
                {isVideo(it) && (
                  <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] uppercase tracking-wider">▶</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
